'use client';

import { useEffect, useRef } from 'react';

import { PROSE_BLOCK_REVEAL_PROPS } from '@/constants/proseBlockReveal';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { createProseBlockReveal } from '@/motion/proseBlockReveal';
import { loadScrollSceneRuntime } from '@/motion/scrollScene';

type ProseBlockRevealProps = {
  children: React.ReactNode;
  className?: string;
};

/**
 * Play-once GSAP island for mid-section prose blocks (pin + SplitText title +
 * prose, optional aside / CTA stagger). Queries marked children inside this
 * root and plays when the block enters the viewport — scroll-timed, not
 * scrubbed. Section headers use `SectionHeaderReveal` instead.
 */
export function ProseBlockReveal({ children, className }: ProseBlockRevealProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const root = rootRef.current;
    if (!root) return;

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    const attach = async () => {
      const runtime = await loadScrollSceneRuntime();
      if (cancelled || !rootRef.current) return;

      const { ScrollTrigger } = runtime;

      const run = () => {
        if (cancelled || !rootRef.current) return;

        const { timeline, cleanup: contentCleanup } = createProseBlockReveal(runtime, {
          root: rootRef.current,
        });

        if (!timeline) {
          contentCleanup();
          return;
        }

        cleanup = contentCleanup;
        ScrollTrigger.refresh();
      };

      if (document.fonts?.status === 'loaded') {
        run();
      } else if (document.fonts?.ready) {
        void document.fonts.ready.then(() => {
          if (!cancelled) run();
        });
      } else {
        run();
      }
    };

    void attach();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [prefersReducedMotion]);

  return (
    <div ref={rootRef} className={className} {...PROSE_BLOCK_REVEAL_PROPS}>
      {children}
    </div>
  );
}
